import API_BASE_URL from '../config/apiConfig';

/**
 * Normalize error messages for invalid or expired tokens/OTPs
 * @param {string} errorMessage - Original error message
 * @returns {string} Normalized error message
 */
const normalizeInvalidExpiredError = (errorMessage) => {
  if (!errorMessage) return 'Invalid or expired. Please request a new one.';

  const lowerMessage = errorMessage.toLowerCase();
  const isInvalidOrExpired =
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('expired') ||
    lowerMessage.includes('expire') ||
    lowerMessage.includes('not valid') ||
    lowerMessage.includes('no longer valid') ||
    lowerMessage.includes('has expired') ||
    lowerMessage.includes('is expired');

  if (isInvalidOrExpired) {
    return 'Invalid or expired. Please request a new one.';
  }

  return errorMessage;
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @param {string} userData.firstName - User's first name
 * @param {string} userData.lastName - User's last name
 * @param {string} userData.idNumber - User's ID number
 * @param {string} userData.dob - Date of birth (YYYY-MM-DD)
 * @param {string} userData.phone - Phone number
 * @param {string} userData.email - Email address
 * @param {string} userData.password - Password
 * @param {string} userData.source - Source of registration (e.g., "APP")
 * @param {string} userData.registerAs - Role to register as (e.g., "LANDLORD", "BNB_HOST", "TENANT", "AGENT")
 * @param {string} userData.referredByCode - Referral code (optional)
 * @returns {Promise<Object>} Registration response
 */
export const registerUser = async (userData) => {
  try {
    const payload = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      idNumber: userData.idNumber,
      dob: userData.dob,
      phone: userData.phone,
      email: userData.email,
      password: userData.password,
      source: userData.source || 'APP',
    };

    // Add registerAs if provided
    if (userData.registerAs) {
      payload.registerAs = userData.registerAs;
    }

    // Add referredByCode if provided (optional)
    if (userData.referredByCode) {
      payload.referredByCode = userData.referredByCode;
    } else {
      // Include empty string if not provided to match API expectations
      payload.referredByCode = '';
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/register`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      false // Registration doesn't require auth
    );

    const data = await response.json();

    if (!response.ok) {
      // Handle specific error messages from API
      const errorMessage = data.message || data.error || 'Registration failed';
      const error = new Error(errorMessage);
      error.response = data; // Attach full response for detailed error handling
      error.status = response.status; // Attach status code
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Registration error:', error);
    // If it's not an API error (network error, etc.), preserve the original error
    if (!error.response) {
      // Network or parsing error
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Log in a user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} Login response with token, refreshToken, user, and roles
 */
export const loginUser = async (email, password) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/auth/login`,
      {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
        }),
      },
      false // Login doesn't require existing auth
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Login failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Extract token, refreshToken, user, and roles from response
    const responseData = data.data || data;
    const token = responseData.token;
    const refreshToken = responseData.refreshToken;
    const user = responseData.user;
    const roles = responseData.roles || [];

    // Save token as Bearer (setAuthToken stores it, apiClient uses it as Bearer)
    if (token) {
      setAuthToken(token);
    }

    // Save refresh token
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    // Return token, refreshToken, user, and roles as required
    return {
      ...data, // Include full response for backward compatibility
      token,
      refreshToken,
      user,
      roles,
      data: {
        ...responseData,
        token,
        refreshToken,
        user,
        roles,
      },
    };
  } catch (error) {
    console.error('Login error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Log in a user with Google ID token
 * @param {string} idToken - Google ID token from Google Sign-In
 * @returns {Promise<Object>} Login response with token, refreshToken, user, roles, accountVerified, requiresProfileCompletion
 */
export const loginWithGoogle = async (idToken) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/auth/google`,
      {
        method: 'POST',
        body: JSON.stringify({
          idToken: idToken,
        }),
      },
      false // Google login doesn't require existing auth
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Google login failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Extract token, refreshToken, user, roles, etc. from response
    const responseData = data.data || data;
    const token = responseData.token;
    const refreshToken = responseData.refreshToken;
    const user = responseData.user;
    const roles = responseData.roles || [];
    const accountVerified = responseData.accountVerified;
    const requiresProfileCompletion = responseData.requiresProfileCompletion;

    // Save tokens
    if (token) {
      setAuthToken(token);
    }
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    // Return full response
    return {
      ...data,
      token,
      refreshToken,
      user,
      roles,
      accountVerified,
      requiresProfileCompletion,
      data: {
        ...responseData,
        token,
        refreshToken,
        user,
        roles,
        accountVerified,
        requiresProfileCompletion,
      },
    };
  } catch (error) {
    console.error('Google login error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get user profile
 * @returns {Promise<Object>} Profile response
 */
export const getProfile = async () => {
  try {
    // Check if token exists before making the request
    const token = getAuthToken();
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/profile`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      // Handle authentication errors (401, 403, or 400 with auth-related messages)
      const errorMessage = data.message || data.error || '';
      const isAuthError = response.status === 401 ||
        response.status === 403 ||
        (response.status === 400 && (
          errorMessage.toLowerCase().includes('invalid') ||
          errorMessage.toLowerCase().includes('uuid') ||
          errorMessage.toLowerCase().includes('anonymous') ||
          errorMessage.toLowerCase().includes('token') ||
          errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('unauthorized')
        ));

      if (isAuthError) {
        // Token is invalid, expired, or malformed
        // Try to refresh if we have a refresh token
        const refreshToken = getRefreshToken();
        if (refreshToken && !isRefreshing) {
          try {
            await refreshAuthToken();
            // Retry the request after refresh
            const retryResponse = await apiClient(
              `${API_BASE_URL}/api/auth/profile`,
              {
                method: 'GET',
              },
              true
            );
            const retryData = await retryResponse.json();
            if (retryResponse.ok) {
              return retryData;
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and throw error
            clearTokensDueToExpiry();
            const normalizedMessage = normalizeInvalidExpiredError(errorMessage);
            const error = new Error(normalizedMessage);
            error.status = response.status;
            error.isAuthError = true;
            throw error;
          }
        }
        // No refresh token or refresh failed
        clearTokensDueToExpiry();
        const normalizedMessage = normalizeInvalidExpiredError(errorMessage);
        const error = new Error(normalizedMessage);
        error.status = response.status;
        error.isAuthError = true;
        throw error;
      }

      const finalErrorMessage = errorMessage || 'Failed to fetch profile';
      const error = new Error(finalErrorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get profile error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update user profile
 * @param {Object} profileData - Profile update data
 * @param {string} profileData.firstName - First name
 * @param {string} profileData.lastName - Last name
 * @param {string} profileData.phone - Phone number
 * @param {string} profileData.dob - Date of birth (YYYY-MM-DD)
 * @param {string} profileData.idNumber - ID number (optional)
 * @param {string} profileData.profilePhotoUrl - Profile photo URL (optional)
 * @param {string} profileData.twoFaMethod - Two-factor authentication method (NONE | OTP_PHONE_EMAIL | AUTHENTICATOR)
 * @param {string} profileData.registerAs - Role to register as (e.g., "LANDLORD", "BNB_HOST", "TENANT", "AGENT")
 * @returns {Promise<Object>} Profile update response
 */
export const updateProfile = async (profileData) => {
  try {
    const payload = {};

    // Add optional fields only if provided
    if (profileData.firstName !== undefined) payload.firstName = profileData.firstName;
    if (profileData.lastName !== undefined) payload.lastName = profileData.lastName;
    if (profileData.phone !== undefined) payload.phone = profileData.phone;
    if (profileData.dob !== undefined) payload.dob = profileData.dob;
    if (profileData.idNumber !== undefined) payload.idNumber = profileData.idNumber;
    if (profileData.profilePhotoUrl !== undefined && profileData.profilePhotoUrl) payload.profilePhotoUrl = profileData.profilePhotoUrl;
    if (profileData.twoFaMethod !== undefined) payload.twoFaMethod = profileData.twoFaMethod;
    if (profileData.registerAs !== undefined) payload.registerAs = profileData.registerAs;

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/profile`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update profile';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update profile error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Resend OTP to authenticated user's registered phone
 * @returns {Promise<Object>} Resend OTP response
 */
export const resendOTP = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/auth/resend-otp`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      true // Resend OTP requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to resend OTP';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Resend OTP error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Verify OTP code
 * @param {string} phone - Phone number
 * @param {string} otp - OTP code
 * @returns {Promise<Object>} OTP verification response
 */
export const verifyOTP = async (phone, otp) => {
  try {
    // OTP verification requires authentication token
    const response = await apiClient(
      `${API_BASE_URL}/api/phone/verify-otp`,
      {
        method: 'POST',
        body: JSON.stringify({
          phone: phone,
          otp: otp,
        }),
      },
      true // OTP verification requires authentication token
    );

    const data = await response.json();

    if (!response.ok) {
      const rawErrorMessage = data.message || data.error || 'OTP verification failed';
      const errorMessage = normalizeInvalidExpiredError(rawErrorMessage);
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Verify OTP error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

// Token storage keys - centralized constants
const TOKEN_STORAGE_KEY = 'tenantscore_auth_token';
const REFRESH_TOKEN_STORAGE_KEY = 'tenantscore_refresh_token';

// Track if a token refresh is in progress to prevent concurrent refresh calls
let isRefreshing = false;
let refreshPromise = null;

/**
 * Get authentication token from localStorage
 * @returns {string|null} JWT token or null
 */
export const getAuthToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    return token;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

/**
 * Store authentication token in localStorage
 * @param {string} token - JWT token to store
 * @returns {boolean} True if stored successfully, false otherwise
 */
export const setAuthToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      return true;
    } else {
      console.warn('Attempted to store empty token');
      return false;
    }
  } catch (error) {
    console.error('Error storing auth token:', error);
    return false;
  }
};

/**
 * Remove authentication token from localStorage
 */
export const removeAuthToken = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Error removing auth token:', error);
  }
};

/**
 * Get refresh token from localStorage
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    return refreshToken;
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return null;
  }
};

/**
 * Store refresh token in localStorage
 * @param {string} refreshToken - Refresh token to store
 * @returns {boolean} True if stored successfully, false otherwise
 */
export const setRefreshToken = (refreshToken) => {
  try {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      return true;
    } else {
      console.warn('Attempted to store empty refresh token');
      return false;
    }
  } catch (error) {
    console.error('Error storing refresh token:', error);
    return false;
  }
};

/**
 * Remove refresh token from localStorage
 */
export const removeRefreshToken = () => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Error removing refresh token:', error);
  }
};

/**
 * Remove both access and refresh tokens (complete logout)
 */
export const clearAllTokens = () => {
  removeAuthToken();
  removeRefreshToken();
  isRefreshing = false;
  refreshPromise = null;
  clearAuthenticatedImageCacheInternal();
};

/**
 * Decode a JWT token payload without verifying its signature.
 * Splits the token on ".", base64url-decodes the payload, and JSON.parses it.
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const decodeJwt = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url -> base64
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Pad with '=' until length is a multiple of 4
    while (payload.length % 4) {
      payload += '=';
    }
    const json = atob(payload);
    // Handle UTF-8 encoded characters
    const decoded = decodeURIComponent(
      json
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Extract roles from the current auth token (JWT `roles` claim).
 * @returns {string[]} Array of role strings (uppercase). Empty if none/unavailable.
 */
export const getRolesFromToken = () => {
  const token = getAuthToken();
  if (!token) return [];
  const payload = decodeJwt(token);
  const roles = payload?.roles || [];
  if (!Array.isArray(roles)) return [];
  return roles.map((r) => String(r).toUpperCase());
};

/**
 * Check if a JWT token is expired or about to expire.
 * Reads the `exp` claim and returns true if expired (or expiring within the skew window).
 * @param {string} [token] - JWT token; defaults to the stored auth token
 * @param {number} [skewSeconds=30] - Treat token as expired if it expires within this many seconds
 * @returns {boolean} True if expired, missing, or invalid
 */
export const isTokenExpired = (token, skewSeconds = 30) => {
  const t = token || getAuthToken();
  if (!t) return true;
  const payload = decodeJwt(t);
  if (!payload) return true;
  if (!payload.exp) return false; // No exp claim — assume valid (can't check)
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp - nowSec <= skewSeconds;
};

/**
 * Log out the current user: revoke the refresh token server-side, then clear local tokens.
 * Always clears local tokens even if the API call fails (best-effort server-side revocation).
 * @returns {Promise<void>}
 */
export const logoutUser = async () => {
  const refreshToken = getRefreshToken();
  try {
    if (refreshToken) {
      // Best-effort server-side revocation. Use requireAuth=true so the current access token
      // is sent (backend may require auth to revoke). If it fails, we still clear locally.
      await apiClient(
        `${API_BASE_URL}/api/auth/logout`,
        {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        },
        true
      );
    }
  } catch (error) {
    // Ignore — we clear tokens locally regardless
    console.error('Logout API call failed (clearing tokens anyway):', error);
  } finally {
    clearAllTokens();
  }
};


/** Custom event name for session expiry (token expired / refresh failed) */
export const SESSION_EXPIRED_EVENT = 'tenantscore_session_expired';

/**
 * Clear tokens and notify the app that the session expired (e.g. token expired or refresh failed).
 * Use this when the backend returns 401 or refresh fails so the app can redirect to login.
 * For user-initiated logout, use clearAllTokens() instead.
 */
export const clearTokensDueToExpiry = () => {
  clearAllTokens();
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  } catch (e) {
    console.warn('Could not dispatch session expired event:', e);
  }
};

/**
 * In-flight + resolved cache so the same image URL is only fetched once per session.
 * Blob URLs stay valid until clearAllTokens() / logout. Do not URL.revokeObjectURL() on
 * these from feature code — revoking breaks the cache and shows broken images.
 */
const authenticatedImageResolved = new Map();
const authenticatedImageInflight = new Map();

function clearAuthenticatedImageCacheInternal() {
  authenticatedImageResolved.forEach((blobUrl) => {
    try {
      if (blobUrl && String(blobUrl).startsWith('blob:')) URL.revokeObjectURL(blobUrl);
    } catch {
      /* ignore */
    }
  });
  authenticatedImageResolved.clear();
  authenticatedImageInflight.clear();
}

function normalizeImageFetchUrl(imageUrl) {
  let normalizedUrl = imageUrl;
  try {
    const urlObj = new URL(imageUrl);
    if (urlObj.protocol === 'http:' && API_BASE_URL.startsWith('https:')) {
      normalizedUrl = imageUrl.replace('http://', 'https://');
    }
    const apiHostname = new URL(API_BASE_URL).hostname;
    if (urlObj.hostname === apiHostname && urlObj.protocol === 'http:' && API_BASE_URL.startsWith('https:')) {
      normalizedUrl = imageUrl.replace('http://', 'https://');
    }
  } catch {
    if (imageUrl.includes('http://') && API_BASE_URL.startsWith('https://')) {
      normalizedUrl = imageUrl.replace('http://', 'https://');
    }
  }
  return normalizedUrl;
}

async function fetchAuthenticatedImageOnce(normalizedUrl) {
  const token = getAuthToken();
  const options = {
    method: 'GET',
    redirect: 'follow',
    mode: 'cors',
  };

  if (token) {
    options.headers = { Authorization: `Bearer ${token}` };
  }

  const response = await fetch(normalizedUrl, options);

  if (!response.ok) {
    console.error('Failed to fetch authenticated image:', response.status, response.statusText);
    return null;
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Refresh authentication token using refresh token
 * @returns {Promise<Object>} Refresh response with new tokens
 */
export const refreshAuthToken = async () => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    const error = new Error('No refresh token available');
    error.isAuthError = true;
    throw error;
  }

  // Set refreshing flag and create promise
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: refreshToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Refresh token expired or invalid - user needs to login again
        clearTokensDueToExpiry();
        const rawErrorMessage = data.message || data.error || 'Session expired';
        const errorMessage = normalizeInvalidExpiredError(rawErrorMessage);
        const error = new Error(errorMessage);
        error.status = response.status;
        error.isAuthError = true;
        throw error;
      }

      // Update tokens
      if (data.data?.token) {
        setAuthToken(data.data.token);
      }
      if (data.data?.refreshToken) {
        setRefreshToken(data.data.refreshToken);
      } else {
        // If no new refresh token provided, keep the old one
        // Some implementations don't rotate refresh tokens
      }

      return data;
    } catch (error) {
      // If refresh fails, clear all tokens
      if (error.isAuthError || error.status === 401 || error.status === 403) {
        clearTokensDueToExpiry();
      }
      throw error;
    } finally {
      // Reset refreshing flag
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Centralized API client that automatically adds authentication token
 * and handles token refresh on 401 errors
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @param {boolean} requireAuth - Whether authentication is required (default: true)
 * @returns {Promise<Response>} Fetch response
 */
export const apiClient = async (url, options = {}, requireAuth = true) => {
  let token = getAuthToken();

  // Build headers
  const headers = {
    ...options.headers,
  };

  // Add Content-Type if not FormData and not already set
  if (!(options.body instanceof FormData) && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Add Authorization header if token exists or if auth is required
  if (requireAuth) {
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      // If auth is required but no token, throw error before making request
      const error = new Error('Authentication required. Please log in.');
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }
  } else if (token) {
    // Even if not required, add token if available (for endpoints that accept optional auth)
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Proactively refresh the token if it's expired/about to expire (avoids a 401 round-trip).
  // Skip for refresh/logout endpoints to avoid recursion.
  if (
    requireAuth &&
    token &&
    !url.includes('/api/auth/refresh') &&
    !url.includes('/api/auth/logout') &&
    getRefreshToken() &&
    isTokenExpired(token)
  ) {
    try {
      await refreshAuthToken();
      token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (refreshError) {
      // Refresh failed — let the request proceed; the 401 handler below will clear tokens.
      // If there's no token after refresh, surface an auth error.
      if (!getAuthToken()) {
        throw refreshError;
      }
    }
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If 401 and we have a refresh token, try to refresh
  if (response.status === 401 && requireAuth && getRefreshToken() && !url.includes('/api/auth/refresh')) {
    try {
      // Attempt token refresh
      await refreshAuthToken();

      // Retry original request with new token
      token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        response = await fetch(url, {
          ...options,
          headers,
        });
      } else {
        // Refresh succeeded but no token returned - should not happen
        const error = new Error('Token refresh failed. Please log in again.');
        error.status = 401;
        error.isAuthError = true;
        throw error;
      }
    } catch (refreshError) {
      // Refresh failed - user needs to login again
      // The error will be thrown and handled by the calling function
      throw refreshError;
    }
  }

  // 401 on protected route with no refresh token (or we didn't try refresh) — clear and notify so app can redirect
  if (response.status === 401 && requireAuth && !url.includes('/api/auth/refresh')) {
    clearTokensDueToExpiry();
  }

  return response;
};

/**
 * Upload profile photo
 * @param {File} file - File to upload
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const getSystemAgreementState = async () => {
  try {
    const response = await apiClient(`${API_BASE_URL}/api/system-agreements/state`, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || data.error || 'Failed to load agreement status');
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get system agreement state error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getActiveSystemAgreement = async () => {
  try {
    const response = await apiClient(`${API_BASE_URL}/api/system-agreements/active`, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || data.error || 'Failed to load agreement');
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get active system agreement error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isAuthError = true;
      throw networkError;
    }
    throw error;
  }
};

export const acceptSystemAgreement = async () => {
  try {
    const response = await apiClient(`${API_BASE_URL}/api/system-agreements/accept`, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || data.error || 'Failed to accept agreement');
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Accept system agreement error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const uploadProfilePhoto = async (file, userId) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please log in again.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('category', 'profile');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Return the full response object with status, message, and data
    return data;
  } catch (error) {
    console.error('Upload profile photo error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Upload KYC file
 * @param {File} file - File to upload
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const uploadKYCFile = async (file, userId) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('category', 'kyc');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Return the full response object with status, message, and data
    return data;
  } catch (error) {
    console.error('Upload KYC file error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Submit KYC documents
 * @param {Object} kycData - KYC submission data with URLs
 * @param {string} kycData.idType - ID type (NATIONAL_ID, PASSPORT, DRIVING_LICENSE)
 * @param {string} kycData.idDocumentFrontUrl - URL of ID front document
 * @param {string} kycData.idDocumentBackUrl - URL of ID back document
 * @param {string} kycData.selfieUrl - URL of selfie document
 * @returns {Promise<Object>} KYC submission response
 */
export const submitKYC = async (kycData) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/kyc/submit`,
      {
        method: 'POST',
        body: JSON.stringify({
          idType: kycData.idType,
          idDocumentFrontUrl: kycData.idDocumentFrontUrl,
          idDocumentBackUrl: kycData.idDocumentBackUrl,
          selfieUrl: kycData.selfieUrl,
        }),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'KYC submission failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;

      // If the error is about phone verification, provide a more helpful message
      if (errorMessage.toLowerCase().includes('verify') && errorMessage.toLowerCase().includes('phone')) {
        error.message = 'Phone verification required. Please go back to the OTP step and verify your phone number before submitting KYC.';
      }

      throw error;
    }

    return data;
  } catch (error) {
    console.error('Submit KYC error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Send login OTP to email or phone
 * @param {string} email - Email address (optional)
 * @param {string} phone - Phone number (optional)
 * @returns {Promise<Object>} OTP send response with token
 */
export const sendLoginOTP = async (email = null, phone = null) => {
  try {
    const payload = {};
    if (email) {
      payload.email = email;
    } else if (phone) {
      payload.phone = phone;
    } else {
      throw new Error('Either email or phone must be provided');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/send-login-otp`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      false // Sending login OTP doesn't require auth
    );

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // If JSON parsing fails, check if response is ok
      if (response.ok) {
        // Response is ok but JSON parsing failed - treat as success with empty data
        console.warn('Send login OTP: Response OK but JSON parsing failed');
        return { status: 'success', message: 'OTP sent successfully' };
      }
      // Response is not ok and JSON parsing failed
      const error = new Error('Failed to send OTP. Please try again.');
      error.status = response.status;
      throw error;
    }

    // If response body indicates success, treat as success regardless of HTTP status
    // This handles cases where API returns 500 but OTP is actually sent
    if (data.status === 'success') {
      // Log warning if HTTP status is not OK (e.g., 500)
      if (!response.ok) {
        console.warn(`Send login OTP: API returned HTTP ${response.status} but response indicates success. OTP was sent.`);
      }
      return data;
    }

    // If HTTP status is OK (200-299), treat as success regardless of message
    if (response.ok) {
      return data;
    }

    // HTTP status indicates failure AND response status is not 'success'
    const errorMessage = data.message || data.error || 'Failed to send OTP';
    const error = new Error(errorMessage);
    error.response = data;
    error.status = response.status;
    throw error;
  } catch (error) {
    console.error('Send login OTP error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Verify login OTP code
 * @param {string} token - Token received from send-login-otp
 * @param {string} otp - OTP code
 * @returns {Promise<Object>} OTP verification response
 */
export const verifyLoginOTP = async (token, otp) => {
  try {
    // For login OTP verification, use the temporary token from send-login-otp
    // But also include stored token if available
    const storedToken = getAuthToken();
    const authToken = token || storedToken;

    // Build custom headers for this specific case
    const headers = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Use fetch directly here since we need to use the temporary token from send-login-otp
    // The apiClient would use the stored token, but we need the temporary one
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-login-otp`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        otp: otp,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const rawErrorMessage = data.message || data.error || 'OTP verification failed';
      const errorMessage = normalizeInvalidExpiredError(rawErrorMessage);
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Verify login OTP error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get current user's own role requests
 * @returns {Promise<Object>} Role requests response with array of user's role requests
 */
export const getMyRoleRequests = async () => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/role-requests/my-requests`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch role requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get my role requests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Request account unsuspension
 * @param {string} reason - Reason for unsuspension request (optional)
 * @returns {Promise<Object>} Unsuspension request response
 */
export const requestUnsuspension = async (reason = '') => {
  try {
    const payload = {};
    if (reason && reason.trim()) {
      payload.reason = reason.trim();
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/account/request-unsuspend`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to submit unsuspension request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Request unsuspension error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Request a role (LANDLORD, BNB_HOST, etc.)
 * @param {string} role - Role to request (should be uppercase: "LANDLORD", "BNB_HOST")
 * @returns {Promise<Object>} Role request response
 */
export const requestRole = async (role) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    // Ensure role is uppercase
    const roleUpper = role.toUpperCase();

    const response = await apiClient(
      `${API_BASE_URL}/api/role-requests/request`,
      {
        method: 'POST',
        body: JSON.stringify({
          role: roleUpper,
        }),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Role request failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Request role error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Check KYB status
 * @returns {Promise<Object>} KYB status response
 */
export const getKYBStatus = async () => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/kyb/status`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to check KYB status';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get KYB status error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Upload KYB file
 * @param {File} file - File to upload
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const uploadKYBFile = async (file, userId) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('category', 'kyb');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Return the full response object with status, message, and data
    return data;
  } catch (error) {
    console.error('Upload KYB file error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Submit KYB documents
 * @param {Array<string>} documentUrls - Array of KYB document URLs (min 1, max 3)
 * @returns {Promise<Object>} KYB submission response
 */
export const submitKYB = async (documentUrls) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please verify your phone number first.');
      error.status = 401;
      throw error;
    }

    if (!documentUrls || !Array.isArray(documentUrls) || documentUrls.length === 0) {
      const error = new Error('At least one document URL is required');
      error.status = 400;
      throw error;
    }

    if (documentUrls.length > 3) {
      const error = new Error('Maximum 3 documents allowed');
      error.status = 400;
      throw error;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/kyb/submit`,
      {
        method: 'POST',
        body: JSON.stringify({
          documentUrls: documentUrls,
        }),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      let errorMessage = data.message || data.error || 'KYB submission failed';
      // Simplify error message if it mentions role requests
      if (errorMessage.toLowerCase().includes('role') && (errorMessage.toLowerCase().includes('landlord') || errorMessage.toLowerCase().includes('bnb_host') || errorMessage.toLowerCase().includes('role-requests'))) {
        errorMessage = 'KYB is only for Landlords and short stay bnb owners. You must register as LANDLORD or BNB_HOST';
      }
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Submit KYB error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Request password update OTP
 * @param {string} email - Email address (optional, can use phone instead)
 * @param {string} phone - Phone number (optional, can use email instead)
 * @returns {Promise<Object>} Request password update response
 */
export const requestPasswordUpdate = async (email = null, phone = null) => {
  try {
    const payload = {};
    if (email) {
      payload.email = email;
    } else if (phone) {
      payload.phone = phone;
    } else {
      throw new Error('Either email or phone must be provided');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/request-password-update`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      false // Request password update doesn't require auth
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to request password update';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Request password update error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reset password with OTP (for forgot password flow)
 * @param {string} phone - Phone number
 * @param {string} otp - OTP code received from request-password-update
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Reset password response
 */
export const resetPassword = async (phone, otp, newPassword) => {
  try {
    const payload = {
      phone: phone,
      otp: otp,
      newPassword: newPassword,
    };

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/reset-password`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      false // Reset password doesn't require auth token
    );

    const data = await response.json();

    if (!response.ok) {
      const rawErrorMessage = data.message || data.error || 'Failed to reset password';
      const errorMessage = normalizeInvalidExpiredError(rawErrorMessage);
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reset password error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Change password with OTP
 * @param {string} email - Email address (optional, can use phone instead)
 * @param {string} phone - Phone number (optional, can use email instead)
 * @param {string} otp - OTP code received from request-password-update
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Change password response
 */
export const changePasswordWithOTP = async (email = null, phone = null, otp, currentPassword, newPassword) => {
  try {
    const payload = {
      otp: otp,
      currentPassword: currentPassword,
      newPassword: newPassword,
    };

    if (email) {
      payload.email = email;
    } else if (phone) {
      payload.phone = phone;
    } else {
      throw new Error('Either email or phone must be provided');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/change-password-with-otp`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      false // Change password with OTP doesn't require auth token
    );

    const data = await response.json();

    if (!response.ok) {
      const rawErrorMessage = data.message || data.error || 'Failed to change password';
      const errorMessage = normalizeInvalidExpiredError(rawErrorMessage);
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Change password with OTP error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Change password (requires authentication)
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Change password response
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    // Check if token exists before making the request
    const token = getAuthToken();
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    const payload = {
      currentPassword: currentPassword,
      newPassword: newPassword,
    };

    const response = await apiClient(
      `${API_BASE_URL}/api/auth/change-password`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
      true // Requires authentication
    );

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // If response is not JSON, create error with status text
      const error = new Error(response.statusText || `Server error (${response.status})`);
      error.status = response.status;
      error.response = { status: response.status, statusText: response.statusText };
      throw error;
    }

    if (!response.ok) {
      // Log the full error response for debugging
      console.error('Change password API error response:', {
        status: response.status,
        statusText: response.status,
        data: data
      });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        clearTokensDueToExpiry();
        const error = new Error('Session expired. Please log in again.');
        error.status = response.status;
        error.isAuthError = true;
        throw error;
      }

      // For 500 errors, extract message from various possible response formats
      if (response.status === 500) {
        const errorMessage = data?.message ||
          data?.error ||
          data?.error?.message ||
          (typeof data === 'string' ? data : null) ||
          'Server error. Please try again later.';
        const error = new Error(errorMessage);
        error.response = data;
        error.status = response.status;
        throw error;
      }

      const errorMessage = data?.message || data?.error || 'Failed to change password';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Change password error:', error);
    // Log full error details for debugging
    if (error.response) {
      console.error('Error response data:', error.response);
    }
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch an authenticated image and convert it to a blob URL
 * This is needed because <img> tags don't automatically include Authorization headers
 * Caches by URL (dedupes parallel + repeat requests) for faster loads.
 * @param {string} imageUrl - URL of the image to fetch
 * @returns {Promise<string>} Blob URL that can be used in img src
 */
export const fetchAuthenticatedImage = async (imageUrl) => {
  if (imageUrl && (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:'))) {
    return imageUrl;
  }
  if (!imageUrl) {
    return null;
  }

  const normalizedUrl = normalizeImageFetchUrl(imageUrl);

  if (authenticatedImageResolved.has(normalizedUrl)) {
    return authenticatedImageResolved.get(normalizedUrl);
  }
  if (authenticatedImageInflight.has(normalizedUrl)) {
    return authenticatedImageInflight.get(normalizedUrl);
  }

  const promise = (async () => {
    try {
      let blobUrl = await fetchAuthenticatedImageOnce(normalizedUrl);
      if (!blobUrl && imageUrl.startsWith('http://')) {
        const httpsUrl = imageUrl.replace('http://', 'https://');
        try {
          blobUrl = await fetchAuthenticatedImageOnce(normalizeImageFetchUrl(httpsUrl));
        } catch {
          /* handled below */
        }
      }
      if (blobUrl) {
        authenticatedImageResolved.set(normalizedUrl, blobUrl);
      }
      return blobUrl;
    } catch (error) {
      console.error('Error fetching authenticated image:', error);
      if (error.message && error.message.includes('CORS') && imageUrl && imageUrl.startsWith('http://')) {
        try {
          const httpsUrl = imageUrl.replace('http://', 'https://');
          const token = getAuthToken();
          if (token) {
            const retryResponse = await fetch(httpsUrl, {
              method: 'GET',
              headers: { Authorization: `Bearer ${token}` },
              redirect: 'follow',
              mode: 'cors',
            });
            if (retryResponse.ok) {
              const blob = await retryResponse.blob();
              const u = URL.createObjectURL(blob);
              authenticatedImageResolved.set(normalizedUrl, u);
              return u;
            }
          }
        } catch (retryError) {
          console.error('Retry with HTTPS also failed:', retryError);
        }
      }
      return null;
    } finally {
      authenticatedImageInflight.delete(normalizedUrl);
    }
  })();

  authenticatedImageInflight.set(normalizedUrl, promise);
  return promise;
};

/**
 * Soft-delete the currently authenticated user account
 * @returns {Promise<Object>} Delete account response
 */
export const deleteAccount = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/account/delete`,
      {
        method: 'DELETE',
      },
      true
    );

    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete account';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete account error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};